import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/authContext";
import { Activity, Lock, Mail, User, Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function fmtErr(detail) {
  if (!detail) return "Erreur inconnue";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" · ");
  return String(detail);
}

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm_password: "" });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Mot de passe : minimum 8 caractères");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      setDone(true);
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail) || "Erreur lors de l'inscription");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#070708] flex items-center justify-center px-4" data-testid="register-success">
        <div className="relative w-full max-w-md">
          <div className="border border-[#CCFF00]/40 bg-[#0A0A0C] rounded-sm p-8 text-center">
            <CheckCircle2 className="w-14 h-14 text-[#CCFF00] mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-display text-2xl font-bold text-white mb-2">Compte créé</h1>
            <p className="text-zinc-400 text-sm mb-6 leading-relaxed">
              Ton inscription a bien été reçue. Un admin doit valider ton compte avant que tu puisses te connecter.
              <br /><br />
              Tu seras notifié dès que c'est validé.
            </p>
            <button onClick={() => nav("/login")} data-testid="register-back-login"
              className="bg-[#CCFF00] text-black font-medium px-6 py-2.5 rounded-sm hover:bg-[#B3E600] glow-volt">
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070708] flex items-center justify-center px-4 py-10" data-testid="register-page">
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 70% 30%, #CCFF00 0%, transparent 50%)" }} />

      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-11 h-11 bg-[#CCFF00] flex items-center justify-center rounded-sm glow-volt">
            <Activity className="w-6 h-6 text-black" strokeWidth={2.2} />
          </div>
          <div>
            <div className="font-display text-xl font-bold tracking-tight text-white leading-none">REACH/OS</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mt-1">Marketing Réseaux</div>
          </div>
        </div>

        <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-8">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-3">// INSCRIPTION</div>
          <h1 className="font-display text-3xl font-bold text-white mb-1">Créer ton compte</h1>
          <p className="text-zinc-400 text-sm mb-7">Inscription gratuite · validation admin sous 24h.</p>

          <form onSubmit={submit} className="space-y-4">
            <Field label="Nom complet" icon={User} testid="reg-name" value={form.name} onChange={v => set("name", v)} placeholder="Jean Dupont" />
            <Field label="Email" icon={Mail} testid="reg-email" type="email" value={form.email} onChange={v => set("email", v)} placeholder="ton@email.com" />

            <div className="space-y-1.5">
              <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Mot de passe (min. 8)</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" strokeWidth={1.5} />
                <input data-testid="reg-password" type={show ? "text" : "password"} required minLength={8} value={form.password} onChange={e => set("password", e.target.value)}
                  placeholder="••••••••" className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm pl-10 pr-10 py-2.5 text-sm text-white placeholder-zinc-700 focus:border-[#CCFF00]/50 outline-none" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Field label="Confirmer le mot de passe" icon={Lock} testid="reg-confirm" type={show ? "text" : "password"}
              value={form.confirm_password} onChange={v => set("confirm_password", v)} placeholder="••••••••" />

            <button type="submit" disabled={loading} data-testid="reg-submit"
              className="w-full bg-[#CCFF00] text-black font-medium py-3 rounded-sm hover:bg-[#B3E600] disabled:opacity-50 flex items-center justify-center gap-2 mt-3 glow-volt transition">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Création..." : "Créer mon compte"}
            </button>
          </form>

          <div className="mt-7 pt-5 border-t border-[#1E1E24] text-center">
            <span className="text-xs text-zinc-500">Déjà un compte ? </span>
            <Link to="/login" data-testid="goto-login" className="text-xs text-[#CCFF00] hover:underline font-medium">Se connecter</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: I, testid, value, onChange, placeholder, type = "text" }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</label>
      <div className="relative">
        <I className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" strokeWidth={1.5} />
        <input data-testid={testid} type={type} required value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm pl-10 pr-3 py-2.5 text-sm text-white placeholder-zinc-700 focus:border-[#CCFF00]/50 outline-none transition" />
      </div>
    </div>
  );
}
